import React, { useEffect, useMemo } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip
} from 'recharts';
import {
  Grid, FormControl, InputLabel, Select, MenuItem, Box, Paper, Typography,
  Button, Card, CardContent, useTheme, alpha, Container
} from '@mui/material';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import {
  BarChart as BarChartIcon, PieChart as PieChartIcon, People, Inventory,
  AddCircle, PersonAdd, AddShoppingCart, Dashboard as DashboardIcon
} from '@mui/icons-material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { getDashboardData, setPeriod } from "../../../app/store/slices/dashboardSlice";

const STATUS_COLORS = ['#43a047', '#fbc02d', '#d32f2f'];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const period = useSelector(state => state.dashboard.period);
  const dashboardData = useSelector(state => state.dashboard.data);

  useEffect(() => {
    dispatch(getDashboardData(period));
  }, [dispatch, period]);

  const salesData = useMemo(() => {
    if (!dashboardData || !dashboardData.TotalSales) {
      return MONTHS.map(month => ({ month, sales: 0 }));
    }
    if (period === "monthly") {
      const salesMap = {};
      dashboardData.TotalSales.forEach(item => {
        salesMap[item.Month] = item.Sales;
      });
      return MONTHS.map(month => ({
        month,
        sales: salesMap[month] || 0,
      }));
    }
    if (period === "weekly") {
      const WEEKS = Array.from({ length: 52 }, (_, i) => `W${i + 1}`);
      const salesMap = {};
      dashboardData.TotalSales.forEach(item => {
        salesMap[item.Week] = item.Sales;
      });
      return WEEKS.map(week => ({
        week,
        sales: salesMap[week] || 0,
      }));
    }
    return dashboardData.TotalSales.map(item => ({
      month: item.Month || item.Week || item.Day || item.Period || item.Year,
      sales: item.Sales,
    }));
  }, [dashboardData, period]);

  const invoiceStatusData = useMemo(() => {
    if (!dashboardData || !dashboardData.InvoicesStatus || dashboardData.InvoicesStatus.length === 0) {
      return [{ name: "No Data", value: 1 }];
    }
    return dashboardData.InvoicesStatus.map(item => ({
      name: item.Name,
      value: item.Value,
    }));
  }, [dashboardData]);

  const topCustomersData = useMemo(() => {
    if (!dashboardData || !dashboardData.TopCustomers || dashboardData.TopCustomers.length === 0) {
      return [{ name: "", amount: 0 }];
    }
    return dashboardData.TopCustomers.map(item => ({
      name: item.Name,
      amount: item.Amount,
    }));
  }, [dashboardData]);

  const topSellingProductsData = useMemo(() => {
    if (!dashboardData || !dashboardData.TopSellingProducts || dashboardData.TopSellingProducts.length === 0) {
      return [{ name: "", sold: 0 }];
    }
    return dashboardData.TopSellingProducts.map(item => ({
      name: item.Name,
      sold: item.Sold,
    }));
  }, [dashboardData]);

  const getXAxisKey = () => {
    if (salesData.length === 0) return "month";
    const keys = Object.keys(salesData[0]);
    return keys.find(k => k !== "sales") || "month";
  };

  const handlePeriodChange = (e) => {
    dispatch(setPeriod(e.target.value));
  };

  const getBarSize = (data) => (data.length === 1 ? 40 : 20);

  const totalSalesAmount = useMemo(() => {
    return salesData.reduce((sum, item) => sum + (item.sales || 0), 0);
  }, [salesData]);

  const totalInvoices = useMemo(() => {
    return invoiceStatusData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [invoiceStatusData]);

  const periodLabel = {
    today: t("today"),
    weekly: t("weekly"),
    monthly: t("monthly"),
    annually: t("annually"),
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
            <DashboardIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" color="primary.main">
                {t("dashboard")}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t("welcome_dashboard")}
              </Typography>
            </Box>
          </Box>
          
          <FormControl size="small" sx={{ 
            minWidth: 160, 
            '& .MuiOutlinedInput-root': { 
              borderRadius: 2,
              backgroundColor: '#fff'
            } 
          }}>
            <InputLabel id="dashboard-period-label">{t("period")}</InputLabel>
            <Select
              labelId="dashboard-period-label"
              value={period}
              label={t("period")}
              onChange={handlePeriodChange}
            >
              <MenuItem value="today">{t("today")}</MenuItem>
              <MenuItem value="weekly">{t("weekly")}</MenuItem>
              <MenuItem value="monthly">{t("monthly")}</MenuItem>
              <MenuItem value="annually">{t("annually")}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<ReceiptIcon />}
            onClick={() => navigate('/tickets/add')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.info.main,
              '&:hover': {
                backgroundColor: theme.palette.info.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
            }}
          >
            {t("ticket_de_caisse")}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircle />}
            onClick={() => navigate('/invoices/add')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.success.main,
              '&:hover': {
                backgroundColor: theme.palette.success.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(56, 142, 60, 0.3)'
            }}
          >
            {t("add_invoice")}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<PersonAdd />}
            onClick={() => navigate('/customers/add')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}
          >
            {t("add_customer")}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddShoppingCart />}
            onClick={() => navigate('/products/add')}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s'
              },
              boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
            }}
          >
            {t("add_product")}
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("totalSales")}
                </Typography>
                <Typography variant="h5" fontWeight="700">
                  €{totalSalesAmount.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {periodLabel[period]} {t("period")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("totalInvoices")}
                </Typography>
                <Typography variant="h5" fontWeight="700">
                  {totalInvoices}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {periodLabel[period]} {t("period")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ 
              height: '100%', 
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)'
              }
            }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("topCustomers")}
                </Typography>
                <Typography variant="h5" fontWeight="700">
                  {topCustomersData.length > 0 && topCustomersData[0].name !== "" ? topCustomersData.length : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {periodLabel[period]} {t("period")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden', 
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`
            }}>
              <BarChartIcon sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("totalSales")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 300, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={salesData}
                  barCategoryGap={salesData.length === 1 ? "60%" : "10%"}
                  margin={{ top: 30, right: 30, left: 20, bottom: 10 }}
                >
                  <XAxis dataKey={getXAxisKey()} />
                  <YAxis />
                  <Tooltip 
                    formatter={(value) => [`€${value}`, t("sales")]}
                    contentStyle={{
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                  />
                  <Bar
                    dataKey="sales"
                    fill="url(#salesGradient)"
                    barSize={getBarSize(salesData)}
                    radius={[4, 4, 0, 0]}
                    label={{
                      position: "top",
                      fill: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  />
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
              {salesData.every(item => item.sales === 0) && (
                <Box sx={{
                  position: "absolute",
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18,
                  fontWeight: 500,
                  zIndex: 2,
                  pointerEvents: "none"
                }}>
                  {t("noSales")}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden', 
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.15)} 100%)`
            }}>
              <PieChartIcon sx={{ color: theme.palette.info.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("invoicesStatus")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 300, position: 'relative' }}>
              {invoiceStatusData.length === 0 ? (
                <Box sx={{
                  height: '100%',
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18
                }}>
                  {t("noInvoicesStatus")}
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={invoiceStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {invoiceStatusData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={STATUS_COLORS[index % STATUS_COLORS.length]} 
                          stroke="none"
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value} ${t("invoices")}`, name]}
                      contentStyle={{
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: 'none'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.15)} 100%)`
            }}>
              <People sx={{ color: theme.palette.warning.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("topCustomers")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 300, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topCustomersData}
                  layout="vertical"
                  barCategoryGap={topCustomersData.length === 1 ? "60%" : "10%"}
                  margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                >
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`€${value}`, t("sales")]}
                    contentStyle={{
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                  />
                  <Bar
                    dataKey="amount"
                    fill="url(#customerGradient)"
                    barSize={getBarSize(topCustomersData)}
                    radius={[0, 4, 4, 0]}
                    label={{
                      position: "right",
                      fill: theme.palette.warning.dark,
                      fontWeight: 600,
                      fontSize: 12,
                      formatter: (value) => `€${value}`
                    }}
                  />
                  <defs>
                    <linearGradient id="customerGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={theme.palette.warning.main} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={theme.palette.warning.main} stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
              {topCustomersData.every(item => item.amount === 0) && (
                <Box sx={{
                  position: "absolute",
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18,
                  fontWeight: 500,
                  zIndex: 2,
                  pointerEvents: "none"
                }}>
                  {t("noTopCustomers")}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            height: '100%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
            }
          }}>
            <Box sx={{ 
              p: 3, 
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.15)} 100%)`
            }}>
              <Inventory sx={{ color: theme.palette.success.main, mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">{t("topProducts")}</Typography>
            </Box>
            <Box sx={{ p: 2, height: 300, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topSellingProductsData}
                  layout="vertical"
                  barCategoryGap={topSellingProductsData.length === 1 ? "60%" : "10%"}
                  margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
                >
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value) => [`${value} ${t("products")}`, t("sales")]}
                    contentStyle={{
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                  />
                  <Bar
                    dataKey="sold"
                    fill="url(#productGradient)"
                    barSize={getBarSize(topSellingProductsData)}
                    radius={[0, 4, 4, 0]}
                    label={{
                      position: "right",
                      fill: theme.palette.success.dark,
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  />
                  <defs>
                    <linearGradient id="productGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={theme.palette.success.main} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={theme.palette.success.main} stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                </BarChart>
              </ResponsiveContainer>
              {topSellingProductsData.every(item => item.sold === 0) && (
                <Box sx={{
                  position: "absolute",
                  top: 0, left: 0, right: 0, bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  fontSize: 18,
                  fontWeight: 500,
                  zIndex: 2,
                  pointerEvents: "none"
                }}>
                  {t("noTopProducts")}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;